import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

const PAGE_SIZES = [25, 50, 100];

// All filter/page/limit state lives in URL params under the given prefix.
// e.g. prefix="mml_" → ?mml_page=2&mml_layer=taustakartta
export default function LogTable({ fetchFn, columns, filters: FilterBar, paramPrefix = '' }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const pk = key => `${paramPrefix}${key}`;

  const page  = parseInt(searchParams.get(pk('page'))  || '1',  10);
  const limit = parseInt(searchParams.get(pk('limit')) || '50', 10);

  // Collect all filter values from URL params (everything except page/limit)
  const filterValues = useMemo(() => {
    const reserved = new Set([pk('page'), pk('limit')]);
    const fv = {};
    searchParams.forEach((val, key) => {
      if (key.startsWith(paramPrefix) && !reserved.has(key)) {
        fv[key.slice(paramPrefix.length)] = val;
      }
    });
    return fv;
  }, [searchParams, paramPrefix]); // eslint-disable-line

  function setParams(updates, resetPage = false) {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (resetPage) next.set(pk('page'), '1');
      Object.entries(updates).forEach(([k, v]) => {
        const key = pk(k);
        if (v === '' || v == null) next.delete(key);
        else next.set(key, String(v));
      });
      return next;
    }, { replace: true });
  }

  useEffect(() => {
    const params = { ...filterValues, page, limit };
    Object.keys(params).forEach(k => { if (params[k] === '' || params[k] == null) delete params[k]; });
    setLoading(true);
    setError(null);
    fetchFn(params)
      .then(r => setData(r.data))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [searchParams]); // eslint-disable-line

  function applyFilters(newFilters) {
    // Clear all old filter params for this prefix, then set new ones
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      const reserved = new Set([pk('page'), pk('limit')]);
      // Remove old filter params
      [...prev.keys()].forEach(k => { if (k.startsWith(paramPrefix) && !reserved.has(k)) next.delete(k); });
      // Set new ones
      next.set(pk('page'), '1');
      Object.entries(newFilters).forEach(([k, v]) => {
        if (v !== '' && v != null) next.set(pk(k), String(v));
      });
      return next;
    }, { replace: true });
  }

  const total = data?.total ?? 0;
  const pages = data?.pages ?? 1;

  return (
    <div>
      {FilterBar && <FilterBar values={filterValues} onApply={applyFilters} />}

      <div className="d-flex align-items-center justify-content-between mb-2 mt-3">
        <span className="text-muted small">{total.toLocaleString('fi-FI')} riviä yhteensä</span>
        <div className="d-flex align-items-center gap-2">
          <span className="text-muted small">Rivejä:</span>
          {PAGE_SIZES.map(s => (
            <button key={s} className={`btn btn-sm ${limit === s ? 'btn-primary' : 'btn-outline-secondary'}`}
              style={{ padding: '1px 8px', fontSize: '0.78rem' }}
              onClick={() => setParams({ limit: s, page: 1 })}>
              {s}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="alert alert-danger py-2">{error}</div>}

      <div className="table-responsive">
        <table className="table table-sm table-hover table-bordered mb-2" style={{ fontSize: '0.8rem' }}>
          <thead className="table-light">
            <tr>{columns.map(c => <th key={c.key} style={c.style}>{c.label}</th>)}</tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={columns.length} className="text-center text-muted py-3">Ladataan...</td></tr>}
            {!loading && data?.rows?.length === 0 && <tr><td colSpan={columns.length} className="text-center text-muted py-3">Ei tuloksia</td></tr>}
            {!loading && data?.rows?.map((row, i) => (
              <tr key={row.id ?? i}>
                {columns.map(c => <td key={c.key} style={c.style}>{c.render ? c.render(row) : row[c.key]}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div className="d-flex align-items-center gap-1 flex-wrap">
          <button className="btn btn-sm btn-outline-secondary" disabled={page <= 1} onClick={() => setParams({ page: page - 1 })}>‹</button>
          {buildPageNums(page, pages).map(pg => (
            <button key={pg} className={`btn btn-sm ${pg === page ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => setParams({ page: pg })}>{pg}</button>
          ))}
          <button className="btn btn-sm btn-outline-secondary" disabled={page >= pages} onClick={() => setParams({ page: page + 1 })}>›</button>
          <span className="text-muted small ms-2">Sivu {page} / {pages}</span>
        </div>
      )}
    </div>
  );
}

function buildPageNums(page, pages) {
  const total = Math.min(pages, 10);
  let start;
  if (pages <= 10) start = 1;
  else if (page <= 5) start = 1;
  else if (page >= pages - 4) start = pages - 9;
  else start = page - 4;
  return Array.from({ length: total }, (_, i) => start + i);
}
